import type { Messages } from './en';
import { ru3 } from './plural';

/**
 * Russian catalogue.
 *
 * Typed as `Messages`, so anything missing — or any function with the wrong
 * arity — fails the build rather than silently showing English.
 */
export const ru: Messages = {
    common: {
        loading: 'Загрузка',
        close: 'Закрыть',
        open: 'открыть ↗',
        nothingYet: 'Пока пусто.',
    },

    locale: { label: 'Язык', en: 'EN', ru: 'RU' },

    join: {
        tagline:
            'Каждый втайне ставит трек в очередь. Комната оценивает его.',
        nameLabel: 'Ваше имя',
        namePlaceholder: 'DJ Набе',
        codeLabel: 'Код комнаты',
        codePlaceholder: 'K7QM2',
        create: 'Создать свою',
        join: 'Войти в комнату',
        connecting: 'Подключение…',
        reconnecting: 'Переподключение…',
        howItWorks: 'Как это работает',
        genericError: 'Не получилось.',
        services:
            'Работает со Spotify, YouTube, Apple Music, Deezer, SoundCloud, TIDAL, Bandcamp и Яндекс Музыкой.',
        findingSeat: 'Ищем ваше место…',
    },

    shell: {
        copyInvite: 'скопировать',
        copied: 'скопировано!',
        copyPrompt: 'Скопируйте ссылку-приглашение:',
        rules: 'Правила',
        leave: 'Выйти',
        live: 'в эфире',
        connecting: 'подключение',
        offline: 'нет связи',
    },

    roster: {
        yourQueue: 'Ваша очередь',
        gone: 'вышел',
        away: 'отошёл',
        ready: 'готов',
        scoreTitle: 'Сколько собрали ваши треки',
        theRoom: 'Комната',
        setlist: 'Сетлист',
        notPlayedYet: 'ещё не играл',
    },

    lobby: {
        queueMore: (n: number) => `Добавьте ещё ${ru3(n, 'трек', 'трека', 'треков')}`,
        setReady: 'Ваш сет готов',
        hintWaiting: (names: string) => `Ждём: ${names}.`,
        hintHostCanStart: 'Все готовы. Опускайте иглу, когда захотите.',
        hintWaitingForHost: 'Все готовы. Ждём, когда ведущий начнёт.',
        setlistLength: (n: number) => `${ru3(n, 'трек', 'трека', 'треков')} за вечер`,
        settings: 'Настройки комнаты',
        tracksQueued: 'Треков у каждого',
        tracksPlayed: 'Из них сыграет',
        fewer: 'Меньше',
        more: 'Больше',
        guessing: 'Угадывание',
        on: 'Вкл',
        off: 'Выкл',
        start: 'Начать вечер',
        needTwo: 'Для угадывания нужно хотя бы два диджея — позовите кого-нибудь или включите режим прослушивания.',
        stillQueueing: (names: string) => `Ещё выбирают: ${names}.`,
    },

    claims: {
        label: 'Ваша заявка',
        yoursOnly: 'Ваша заявка. До раскрытия её никто, кроме вас, не видит.',
        needsPlayers: (n: number) => `Нужно хотя бы ${n} игрока в комнате`,
        needsGuessing: 'Нужно включить угадывание',
        evidence: {
            'never-heard': (n: number, of: number) => `${n} из ${of} не слышали`,
            unanimous: (n: number, of: number) => `${n} из ${of} поставили 4 и выше`,
            emotional: (n: number, of: number) => `${n} из ${of} зацепило`,
            'so-me': (n: number, of: number) => `${n} из ${of} вас угадали`,
            'not-me': (n: number, of: number) => `${n} из ${of} подумали на другого`,
            divisive: (n: number, of: number) => `${n} из ${of}`,
        },
        evidenceSpread: (percent: number) => `разброс ${percent}% от максимально возможного`,
        spent: 'Уже поставлена на другой трек',
        handLabel: 'Ваши карты',
        left: (n: number) => `осталось: ${n}`,
        spentOn: (n: number) => `потрачена на трек ${n}`,
        dropCard: 'Положите сюда карту',
        orLeaveSafe: 'или оставьте как есть',
        sealed: 'запечатано',
        none: {
            title: 'Без заявки',
            blurb: 'Просто трек. Сколько комната дала — столько и получите.',
        },
        'never-heard': {
            title: 'Вы такого не слышали',
            blurb: 'Чем меньше людей слышали трек раньше, тем больше он принесёт. Но понравиться он всё равно должен.',
        },
        unanimous: {
            title: 'Понравится всем',
            blurb: 'Считается, сколько человек поставили четыре или пять. Хватит одного недовольного, чтобы вы потеряли.',
        },
        emotional: {
            title: 'Это вас проймёт',
            blurb: 'Считается, скольких трек зацепил. Понравился он им или нет — неважно.',
        },
        'so-me': {
            title: 'Вы поймёте, что это я',
            blurb: 'Чем больше людей угадают, что трек ваш, тем больше он принесёт.',
        },
        'not-me': {
            title: 'Вы не угадаете, что это я',
            blurb: 'Платит за то, что вас не раскусили. Если никто вообще не гадал — не платит ничего.',
        },
        divisive: {
            title: 'Расколет комнату',
            blurb: 'Нужно, чтобы одним трек зашёл, а другим совсем нет, примерно поровну. Если все согласны — не считается.',
        },
    },

    url: {
        placeholder: 'Вставьте ссылку на Spotify, YouTube, Apple Music…',
        submit: 'В очередь',
        hintInvalid: 'Это пока не похоже на ссылку.',
        hintUnsupported:
            'Этот сервис мы не проигрываем. Попробуйте Spotify, YouTube, Apple Music, Deezer, SoundCloud, TIDAL или Яндекс.',
        hintLooking: 'Ищем информацию…',
        hintGood: 'Отлично.',
        hintReady: 'Можно добавлять.',
        loading: 'Загрузка…',
    },

    tracks: {
        remove: 'Убрать',
        playingNow: 'Играет сейчас',
    },

    drums: {
        title: 'Драм-машина',
        silent: 'без звука',
        play: 'Пуск',
        stop: 'Стоп',
        clear: 'Сброс',
    },

    round: {
        position: (n: number, total: number) => `Трек ${n} / ${total}`,
        inRoom: (n: number) => `в комнате: ${n}`,
        onFloor: (n: number) => `вышли: ${n}`,

        nowPlaying: 'Сейчас играет',
        embedOf: (service: string) => `${service} · плеер`,
        embedGeneric: 'плеер',
        lookupFailed: 'Не удалось прочитать ссылку. Попробуем ещё раз до начала.',

        whoPutThisOn: 'Кто это поставил',
        sealedDj: (n: number) => `Один из ${n}`,
        putOn: (title: string) => `Поставил — ${title}`,
        namedBy: 'Угадали',

        djsClaim: 'Заявка диджея',
        sealedUntilReveal: 'Запечатано до раскрытия',
        stampSealed: 'запечатано',
        stampYours: 'видите только вы',
        stampLanded: (m: string) => `сыграла · ×${m}`,
        stampFlat: 'без изменений',

        // Только счётчики, никаких имён: диджей — единственный, кто не может
        // ответить, поэтому список ответивших назвал бы его по отсутствию.
        answersSealed: (n: number, of: number) => `Ответы запечатаны — ${n} из ${of}`,
        answerSlot: (n: number) => `Ответ ${String(n).padStart(2, '0')}`,
        stillOut: (n: number) => `ещё не ответили: ${n}`,

        yourAnswer: 'Ваш ответ',

        yoursEyebrow: 'Этот — ваш',
        yoursLead: 'Остаётся только смотреть, как они разбираются.',
        findingTrack: 'Ищем трек…',
        openIn: (service: string) => `Открыть в ${service} ↗`,
        openLink: 'Открыть ссылку',
        done: 'Готово',
        undone: 'Ещё не готов',
        doneCount: (n: number, total: number) => `Готовы ${n} из ${total}`,
        timeLeft: 'Сколько осталось на раунд',
        hostReveal: 'Раскрыть диджея',
        hostNext: 'Следующий трек',
        hostFinish: 'Завершить вечер',
        hostWaiting: 'Ждём, пока комната закончит.',
        waitingForNext: 'Ждём, когда ведущий поставит следующий трек…',
        waitingForReveal: 'Все проголосовали. Ждём раскрытия ведущим…',
    },

    answer: {
        likeTitle: 'Насколько понравилось?',
        likeValue: (n: number) => `${n} из 5`,
        // По фразе на каждое число, чтобы оценка значила у всех одно и то же.
        verdicts: ['Совсем никак', 'Не моё', 'Так себе', 'Неплохо', 'Очень хорошо', 'Отлично'],
        heard: 'Слышал раньше',
        heardHint: 'Вы слышали этот трек до сегодняшнего вечера?',
        popular: 'Популярный',
        popularHint: 'Это известный трек? Чем больше людей так ответят, тем меньше он принесёт диджею.',
        yes: 'да',
        no: 'нет',
        moved: 'Зацепило',
        movedHint: 'Трек вас пробрал? Понравился он вам или нет — неважно.',
        unsigned: 'Поставьте оценку, чтобы ответ засчитался.',
    },

    guess: {
        title: 'Чей трек?',
    },

    reveal: {
        correct: 'верно',
        wrong: 'мимо',
        likedIt: (name: string, n: number) => `${name} поставил ${n} из 5`,
        sumLabel: 'Что дал каждый слушатель',
        nobodyAnswered: 'никто не ответил',
        scaleLabel: 'Среднее, потом на весь стол',
        scaleWorking: (total: number, answered: number, listeners: number) =>
            `${total} ÷ ${answered} ответивших × ${listeners} слушателей`,
        nothingToScale: 'нечего пересчитывать',
        dragWorking: (n: number, of: number) => `${n} из ${of} назвали популярным`,
        timesWorking: (m: string) => `умножено на ${m}`,
        noClaim: 'заявки не было',
        unchanged: 'без изменений',
        totalLabel: (name: string) => `${name} получает`,
        rowWorking: (name: string) => `${name}: оценка, плюс очко за новинку, плюс два, если зацепило`,
        newTag: (n: number) => `новинка +${n}`,
        movedTag: (n: number) => `зацепило +${n}`,
        saidNothing: 'ничего не сказал',
        answersTitle: 'Что сказала комната',
        obviousLabel: 'Популярность',
        noDrag: 'популярным его никто не назвал',
        silent: (n: number) => `промолчали: ${ru3(n, 'слушатель', 'слушателя', 'слушателей')}`,
        heardTag: 'слышал',
        popularTag: 'популярный',
        toughCrowd: 'Никто ничего не ответил.',
        answeredSummary: (answered: number, silent: number) =>
            `ответили: ${answered} · промолчали: ${silent}`,
        djCouldNotAnswer: (name: string) => `${name} не мог отвечать`,
        namedTheDj: 'угадал диджея',
        guessWorth: '+1 ему',

        theWorking: 'Как это посчитано',
        claimLabel: 'Заявка',

        roomGave: '♥ Сколько дала комната',
        djBanked: '★ Сколько забрал диджей',
        // Заявки умеют уходить в минус, поэтому знак берём из числа.
    },

    finale: {
        thatWasTheNight: 'Вот и всё.',
        roomSummary: (tracks: number, players: number) =>
            `${ru3(tracks, 'трек', 'трека', 'треков')} · вас было ${players}`,
        rankedBy: 'Треки',
        starRanksNothing: '♥ комната · ★ диджей',
        played: (n: number) => `играл ${String(n).padStart(2, '0')}`,
        notGiven: 'Не вручена',
        youGaveIt: (dj: string, like: number) => `${dj} · ${like}`,
        copyAll: (n: number) => `скопировать (${n})`,

        awards: 'Награды',
        takeaway: 'Что вы уносите',
        takeawayEmpty: 'Сегодня вы никому не поставили 4. Честно — значит честно; будет ещё вечер.',
        copyTakeaway: 'Скопировать свой плейлист',
        copyTakeawayHeader: (room: string) => `Secret DJ — мой улов из комнаты ${room}`,
        loveTitle: 'Сколько дала комната',
        totalTitle: 'Во что вышло после заявки',
        unplayed: 'Те, что не сыграли',
        leave: 'Выйти из комнаты',
        copyPrompt: 'Скопируйте сет-лист вечера:',
    },

    awards: {
        'track-of-the-night': {
            title: 'Трек вечера',
            blurb: 'больше всех ♥ за вечер',
        },
        'deep-cut': {
            title: 'Находка',
            blurb: 'никто не слышал — и зашло',
        },
        'gut-punch': {
            title: 'Под дых',
            blurb: 'зацепил больше всех',
        },
        'human-shazam': {
            title: 'Живой Shazam',
            blurb: 'чаще всех угадывал диджея',
        },
        ghost: {
            title: 'Призрак',
            blurb: 'его не угадали',
        },
    },

    awardValue: {
        points: (n: number) => ru3(n, 'очко', 'очка', 'очков'),
        guesses: (n: number) => `${ru3(n, 'верная догадка', 'верные догадки', 'верных догадок')}`,
        percent: (n: number) => `${n}%`,
        listeners: (n: number) => ru3(n, 'слушатель', 'слушателя', 'слушателей'),
    },

    rules: {
        title: 'Правила',
        oath: 'Не врите в ответах. Слышали вы трек раньше или нет, зацепил он вас или нет — знаете только вы, проверить это никто не может. Начнёте врать — игра перестанет работать, в ней просто ничего не останется. Это не соревнование, это совместный опыт. Будьте честны, будьте добры и получайте удовольствие.',
        items: [
            {
                head: 'Выбирайте втайне.',
                body: 'Все ставят в очередь одинаковое число треков, но сыграют не все — какие именно, решает случай. Чужую очередь никто не видит.',
            },
            {
                head: 'Сделайте заявку.',
                body: 'К своему треку можно приписать заявку — обещание, что он сделает с комнатой. Угадали — получите в полтора раза больше, промахнулись — вдвое меньше. Без заявки получаете ровно то, что дала комната: не рискуете, но и не выигрываете. Заявку до раскрытия никто не видит.',
            },
            {
                head: 'Отвечайте честно.',
                body: 'На каждый трек четыре вопроса: насколько понравилось, слышали ли раньше, популярный ли он, зацепило ли вас. Правильный ответ знаете только вы. Врать бессмысленно: на раскрытии все ответы показывают с именами.',
            },
            {
                head: 'Оценка от нуля до пяти.',
                body: 'Насколько вам зашло. Пока оценка не поставлена, ответ не засчитывается вообще. Не ответили совсем — вас просто не учитывают, диджей от этого ничего не теряет.',
            },
            {
                head: 'Налог на популярность.',
                body: 'Чем больше людей отметят трек популярным, тем меньше он принесёт — вплоть до половины. Поставить известный хит — не способ выиграть по-лёгкому.',
            },
            {
                head: 'Угадайте диджея.',
                body: 'Все, кроме диджея, выбирают, чей это трек. Угадали — плюс очко вам. И в любом случае это кормит заявку диджея и награды в конце.',
            },
            {
                head: 'Счёт один.',
                body: 'Ваши треки с учётом заявки плюс очко за каждого угаданного диджея. Таблицы в конце нет: счёт нужен, чтобы выборы во время вечера имели вес, а не чтобы ранжировать комнату.',
            },
        ],
        claimsTitle: 'Какие бывают заявки',
        claimsBody: 'Каждую можно поставить один раз за вечер, поэтому весь выбор — какому из ваших треков какая достанется. Любая из них умножает всю сумму: сбылась полностью — очков в полтора раза больше, не сбылась совсем — вдвое меньше.',
        scoring: {
            title: 'Из чего складываются очки',
            addTitle: 'Каждый ответивший слушатель приносит',
            add: [
                { what: 'Насколько ему понравилось', points: 'от 0 до 5' },
                { what: 'Он не слышал трек раньше', points: '+1' },
                { what: 'Его зацепило', points: '+2' },
            ],
            addNote: 'Это складывают, делят на число ответивших и умножают обратно на количество слушателей за столом. Поэтому если кто-то промолчал, диджей от этого ничего не теряет.',
            obviousTitle: 'Дальше: налог на популярность',
            obviousBody: 'Чем больше слушателей отметили «популярный», тем меньше становится сумма. Никто не отметил — ничего не меняется. Отметила половина — минус четверть. Отметили все — за трек дадут вдвое меньше.',
            claimTitle: 'Дальше: ваша заявка',
            claimBody: 'Заявка умножает то, что осталось. Сбылась полностью — очков в полтора раза больше. Сбылась наполовину — ничего не меняется. Не сбылась совсем — вдвое меньше. Трек без заявки не трогают вообще, поэтому «без заявки» и есть безопасный вариант.',
            guessLine: { what: 'Вы угадали диджея', points: '+1, вам' },
            example:
                'Четыре слушателя, ответили все: 5, 4, 4 и 3 — это 16. Никто раньше трек не слышал, значит +1 каждому — 20. Двоих зацепило, ещё +2 каждому — 24. «Популярный» не отметил никто, так что 24 и остаётся. Заявка «Вы такого не слышали» сбылась полностью, очков в полтора раза больше — 36 очков. А если бы двое из четверых её отметили, из 24 вышло бы 18, а после заявки — 27.',
        },
        note: 'Темп задаёт ведущий: он начинает игру, раскрывает раунды и включает следующий трек. Если ведущий уйдёт или пропадёт, ведущим автоматически станет кто-то другой.',
    },

    feed: {
        joined: (name: string) => `${name} присоединился`,
        left: (name: string) => `${name} вышел`,
        hostChanged: (name: string) => `${name} теперь ведущий`,
        finished: 'Вот и всё — результаты готовы',
    },

    errors: {
        BAD_REQUEST: 'Непонятный запрос.',
        GAME_NOT_FOUND: 'Комнаты с таким кодом нет. Проверьте написание.',
        GAME_FULL: 'Комната заполнена.',
        GAME_ALREADY_STARTED: 'Игра уже идёт.',
        NAME_TAKEN: 'В этой комнате уже есть игрок с таким именем.',
        NAME_INVALID: 'Имя из 2–20 букв, цифр, пробелов или - _ . апострофа.',
        SESSION_INVALID: 'Ваше место истекло. Войдите заново.',
        NOT_AUTHENTICATED: 'Вы ещё не в комнате.',
        NOT_HOST: 'Это может только ведущий.',
        ROUND_NOT_READY: 'Кто-то в комнате ещё не закончил.',
        WRONG_PHASE: 'Сейчас так нельзя.',
        NOT_READY: 'Сначала все должны собрать очередь.',
        NOT_ENOUGH_PLAYERS: 'Для угадывания нужно хотя бы два диджея. Позовите кого-нибудь или включите режим прослушивания.',
        TRACK_LIMIT_REACHED: 'Вы уже добавили все свои треки.',
        TRACK_DUPLICATE: 'Этот трек уже есть в игре.',
        TRACK_UNSUPPORTED: 'Эту ссылку мы проиграть не можем.',
        TRACK_URL_INVALID: 'Ссылка не подходит.',
        TRACK_NOT_FOUND: 'Такого трека в вашей очереди нет.',
        VOTE_NOT_ALLOWED: 'В этом раунде вы не голосуете.',
        VOTE_OWN_TRACK: 'Это ваш трек. Просто наслаждайтесь.',
        VOTE_INVALID_TARGET: 'За этого игрока голосовать нельзя.',
        CLAIM_INVALID: 'Такую заявку сделать нельзя.',
        CLAIM_SPENT: 'Эта заявка уже стоит на другом вашем треке.',
        CLAIM_UNAVAILABLE: 'Для этой заявки нужен стол побольше или включённое угадывание.',
        ANSWER_OWN_TRACK: 'Отвечать про свой трек — жульничество и немного грустно.',
        RATING_INVALID: 'Оценка — это целое число от нуля до пяти.',
        RATE_LIMITED: 'Помедленнее.',
        INTERNAL: 'У нас что-то сломалось.',
        TIMEOUT: 'Сервер не ответил. Проверьте соединение.',
    },

    embed: {
        'unrecognised-link': 'Не узнаём эту ссылку.',
        'missing-youtube-id': 'В ссылке YouTube нет идентификатора видео.',
        'missing-spotify-id': 'В ссылке нет идентификатора Spotify.',
        'missing-deezer-id': 'В ссылке нет идентификатора Deezer.',
        'missing-apple-id': 'В ссылке нет идентификатора Apple Music.',
        'missing-yandex-id': 'Ссылке Яндекса нужен альбом или трек.',
        'missing-tidal-id': 'В ссылке нет идентификатора TIDAL.',
        'external-only': 'Это откроется в отдельной вкладке.',
    },
};
